import { FC, ReactElement, useEffect, useState, useMemo } from 'react';
import { Form } from 'react-final-form';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { BsPlus } from 'react-icons/bs';
import { omit } from 'lodash';
import { FormApi, MutableState } from 'final-form';
import { v4 as uuidv4 } from 'uuid';

import { Box, Button } from '@qwealth/qcore';
import { loadTeam, QTask, selectUser, TASK_COMPLETED } from '@qwealth/qdata';
import { uploadFiles } from '@qwealth/qvault';

import { useAppDispatch, useAppSelector } from 'data/store';
import { saveActivityTask } from 'data/actions/activities';
import { saveHouseholdTask } from 'data/actions/householdActivities';
import { QTaskForm, toQTask, getCurrentUserTagsKey } from 'data/models/taskForm';
import { buildNoteContent, useInitializedEditorState } from 'hooks/useInitializedEditorState';
import { validateTask } from 'data/validations/taskValidations';
import { DEFAULT_TASK, getTagKeys, getTaskAttachmentPath } from 'data/helpers/taskHelper';
import {
  ModalModeOptions,
  MODAL_MODE_VIEW,
  MODAL_MODE_EDIT,
  MODAL_MODE_ADD,
  ACTION_CONFIRMATION_EDIT,
  TaskTabOptions,
  TASK_TAB_COMMENTS,
  TASK_TAB_QNOTES,
} from 'data/models/Activity';
import TaskSidebar from './TaskSidebar';
import MainSectionDisplayMode from './MainSectionDisplayMode';
import MainSectionAddAndEditMode from './MainSectionAddAndEditMode';
import { getSavedFilters, getTaskSavedFilters } from 'utils/activityFilterHelper';
import { HouseholdActivityFilters, QFeedTaskFilterParam } from 'data/models/ActivityFilters';
import RecurringTaskActionConfirmation from './RecurringTaskActionConfirmation';
import { loadHouseholdNotes } from 'data/actions/qnotes';
import { Spinner } from 'react-bootstrap';
import { TaskActions } from 'components/common/activityActions';
import TransitionBox from 'components/Households/Common/QNotes/TransitionBox';
import { selectIsAssignedWorkflowInStore } from 'data/selectors/workflows';
import { fetchAndUpdateAssignedWorkflow } from 'data/helpers/workflows';
interface Props {
  isQFeed?: boolean;
  close?: () => void;
  task: Partial<QTask> | null;
  externalModeOption?: ModalModeOptions;
  setIsTaskModalHidden?: React.Dispatch<React.SetStateAction<boolean>>;
  taskActions?: TaskActions;
  isTaskModalFromWorkflow?: boolean;
  setShowWorkflowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddTask: FC<Props> = ({
  task,
  isQFeed = false,
  close,
  externalModeOption = MODAL_MODE_VIEW,
  setIsTaskModalHidden,
  taskActions,
  isTaskModalFromWorkflow,
  setShowWorkflowModal,
}): ReactElement => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const isTaskWorkflowInStore = useAppSelector(selectIsAssignedWorkflowInStore(task?.workflowId));
  const [modalMode, setModalMode] = useState<ModalModeOptions>(externalModeOption);
  const [editorState, setEditorState] = useInitializedEditorState(task);
  const [initialEditorState, setInitialEditorState] = useInitializedEditorState(task);
  const [taskContentHtml, setTaskContentHtml] = useState<string>('');
  const [qNoteModalMode, setQNoteModalMode] = useState<ModalModeOptions>(MODAL_MODE_VIEW);
  const [selectedTab, setSelectedTab] = useState<TaskTabOptions>(TASK_TAB_COMMENTS);
  const [taskAttachmentsPath, setTaskAttachmentsPath] = useState<string>('');

  const qBoardFilters: QFeedTaskFilterParam = {
    ...getTaskSavedFilters(),
    userEmail: user.email,
  };
  const householdFilters: Omit<HouseholdActivityFilters, 'timeZone'> = {
    ...getSavedFilters(),
    householdQID: task?.householdQID ?? '',
  };

  const getHtmlFromTaskContentState = async (values: Partial<QTask>) => {
    const taskContentState = await buildNoteContent(values);
    const rawTaskContentState = convertToRaw(taskContentState.getCurrentContent());
    const taskContentHtml = draftToHtml(rawTaskContentState, undefined, undefined, (entity, text) => {
      if (entity.type === 'LINK') {
        return `<a href="${entity.data.url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }
      return undefined;
    });
    setTaskContentHtml(taskContentHtml);
  };

  const deleteFollowingTasks = (
    [value]: [boolean],
    state: MutableState<QTaskForm, Partial<QTaskForm>>,
    { changeValue }: { changeValue: (state: MutableState<QTaskForm, Partial<QTaskForm>>, name: string, mutate: (value: any) => any) => void },
  ) => {
    changeValue(state, 'deleteFollowingTasks', () => value);
  };

  const editFollowingFields = (
    [value]: [boolean],
    state: MutableState<QTaskForm, Partial<QTaskForm>>,
    { changeValue }: { changeValue: (state: MutableState<QTaskForm, Partial<QTaskForm>>, name: string, mutate: (value: any) => any) => void },
  ) => {
    changeValue(state, 'editFollowingFields', () => value);
  };

  const initialFormData: QTaskForm = useMemo(
    () => ({
      ...DEFAULT_TASK,
      partnerID: user.partnerID,
      ...task,
      tags: getTagKeys(task?.taskTags),
    }),
    [task],
  );
  const TransitionBoxKey = useMemo(() => initialFormData.id ?? uuidv4(), [initialFormData.id]);

  const saveTask = async (values: QTaskForm, form: FormApi<QTaskForm, Partial<QTaskForm>>) => {
    const taskValues = {
      ...values,
      startDate: values.dueDate,
    };

    const taskToSave = values.isRecurring ? taskValues : omit(taskValues, ['frequency', 'startDate', 'endDate']);

    const payload = toQTask(taskToSave, editorState);

    const savedTasks = isQFeed
      ? await dispatch(saveActivityTask(payload, qBoardFilters))
      : await dispatch(saveHouseholdTask(payload, householdFilters));

    if (!savedTasks) {
      return;
    }

    const savedTask = {
      ...savedTasks[0],
      tags: getCurrentUserTagsKey(savedTasks[0].taskTags, user.partnerID),
    };

    taskActions?.saveTask?.(savedTask);

    if (isTaskWorkflowInStore && !isTaskModalFromWorkflow) {
      if (savedTask.workflowId && (initialFormData.status !== savedTask.status || initialFormData.dueDate !== savedTask.dueDate)) {
        await fetchAndUpdateAssignedWorkflow(savedTask.workflowId, dispatch);
      }
    }

    if (modalMode === MODAL_MODE_EDIT) {
      if (savedTask) {
        form.restart({
          ...initialFormData,
          ...savedTask,
        });
        getHtmlFromTaskContentState(savedTask);
        setInitialEditorState(editorState);
      }
    }

    const taskId = savedTask?.recurringTaskId ? `RecurringTask${savedTask.recurringTaskId}` : `${savedTask.id}`;
    if (values.pending && taskId && payload.householdQID) {
      const path = getTaskAttachmentPath(payload.householdQID, taskId);
      await uploadFiles(values.pending, path);
    }

    if (modalMode === MODAL_MODE_ADD) {
      close?.();
      taskActions?.cancelTask?.();
    } else {
      setModalMode(MODAL_MODE_VIEW);
      taskActions?.viewTask?.();
    }
  };

  const householdCode = task?.householdIaCode ?? task?.householdInsuranceCode;

  useEffect(() => {
    if (modalMode === MODAL_MODE_VIEW && task) {
      getHtmlFromTaskContentState(task);
    }
  }, [task]);

  useEffect(() => {
    setModalMode(externalModeOption);
  }, [externalModeOption]);

  useEffect(() => {
    if (isQFeed && householdCode) {
      dispatch(loadTeam(householdCode));
    }
  }, [householdCode, dispatch, isQFeed]);

  useEffect(() => {
    if (task?.householdQID && isQFeed) {
      const householdQID = task.householdQID;
      (async () => {
        await dispatch(loadHouseholdNotes(householdQID));
      })();
    }
  }, [task?.householdQID, isQFeed, dispatch]);

  useEffect(() => {
    if (task?.householdQID && (task?.recurringTaskId || task?.id)) {
      const taskId = task?.recurringTaskId ? `RecurringTask${task.recurringTaskId}` : `${task?.id}`;

      if (taskId) {
        const path = getTaskAttachmentPath(task.householdQID, taskId);
        setTaskAttachmentsPath(path);
      } else {
        setTaskAttachmentsPath('');
      }
    } else {
      setTaskAttachmentsPath('');
    }
  }, [task]);

  useEffect(() => {
    setSelectedTab(TASK_TAB_COMMENTS);
  }, [task?.id]);

  const handleCancel = (form: FormApi<QTaskForm, Partial<QTaskForm>>) => {
    if (modalMode === MODAL_MODE_ADD) {
      close && close();
      taskActions?.cancelTask?.();
    } else {
      setEditorState(initialEditorState);
      setModalMode(MODAL_MODE_VIEW);
      taskActions?.viewTask?.();
      form.reset();
    }
  };
  return (
    <Form<QTaskForm>
      initialValues={initialFormData}
      onSubmit={(values, form) => saveTask(values, form)}
      validate={values => validateTask(values, editorState)}
      mutators={{ deleteFollowingTasks, editFollowingFields }}
    >
      {({ handleSubmit, values, submitting, form, dirty, invalid }) => {
        return (
          <TransitionBox padding="default" index={1} isVisible={true} key={TransitionBoxKey}>
            <Box display="flex" flexDirection="row" mb="default" gap="largest">
              {modalMode === MODAL_MODE_VIEW && (
                <MainSectionDisplayMode
                  task={task}
                  formValues={values}
                  isQFeed={isQFeed}
                  setModalMode={setModalMode}
                  taskContentHtml={taskContentHtml}
                  close={close}
                  form={form}
                  setIsTaskModalHidden={setIsTaskModalHidden}
                  qNoteModalMode={qNoteModalMode}
                  setQNoteModalMode={setQNoteModalMode}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                  taskAttachmentsPath={taskAttachmentsPath}
                  taskActions={taskActions}
                  isTaskModalFromWorkflow={isTaskModalFromWorkflow}
                />
              )}

              {modalMode !== MODAL_MODE_VIEW && (
                <MainSectionAddAndEditMode
                  task={task}
                  editorState={editorState}
                  formValues={values}
                  setEditorState={setEditorState}
                  taskAttachmentsPath={taskAttachmentsPath}
                />
              )}

              <TaskSidebar
                modalMode={modalMode}
                task={task}
                formValues={values}
                isQFeed={isQFeed}
                isTaskModalFromWorkflow={isTaskModalFromWorkflow}
                setShowWorkflowModal={setShowWorkflowModal}
                close={close}
              />
            </Box>
            <Box display="flex" justifyContent={modalMode === MODAL_MODE_VIEW ? 'space-between' : 'flex-end'} mt="largest">
              {modalMode === MODAL_MODE_VIEW && selectedTab === TASK_TAB_QNOTES && values.status !== TASK_COMPLETED && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setQNoteModalMode(MODAL_MODE_ADD);
                  }}
                >
                  <Box display="flex" flexDirection="row" paddingLeft="default" paddingRight="large" alignItems="center">
                    <BsPlus size="25px" strokeWidth="0.5" />
                    Related QNote
                  </Box>
                </Button>
              )}
              {modalMode !== MODAL_MODE_VIEW && (
                <Box display="flex" gap="default">
                  {!(modalMode === MODAL_MODE_EDIT && values.isRecurring) && (
                    <Button mr="default" onClick={handleSubmit} disabled={!dirty || invalid || submitting}>
                      {modalMode === MODAL_MODE_ADD ? 'Create' : 'Save'}
                      {submitting && <Spinner />}
                    </Button>
                  )}
                  {modalMode === MODAL_MODE_EDIT && values.isRecurring && (
                    <RecurringTaskActionConfirmation
                      primaryButtonActionHandler={() => {
                        form.mutators.editFollowingFields(false);
                        handleSubmit();
                      }}
                      secondaryButtonActionHandler={() => {
                        form.mutators.editFollowingFields(true);
                        handleSubmit();
                      }}
                      taskTitle={values?.title}
                      actionConfirmation={ACTION_CONFIRMATION_EDIT}
                      ActionButtonRenderer={openHandler => (
                        <Button
                          disabled={!dirty || invalid || submitting}
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            openHandler();
                          }}
                        >
                          Save
                          {submitting && <Spinner />}
                        </Button>
                      )}
                      setIsTaskModalHidden={setIsTaskModalHidden}
                    />
                  )}
                  <Button variant="outline" onClick={() => handleCancel(form)}>
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          </TransitionBox>
        );
      }}
    </Form>
  );
};

export default AddTask;
